import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Paper, Slider, Stack, Tab, Typography } from "@mui/material";
import { TabContext } from "@mui/lab";
import { BADGE_PAPER_STYLES } from "../../_app/consts";
import Box from "@mui/material/Box";
import CustomPaper from "../CustomPaper";
import AdronTypography from "../AdronTypography";
import { TabsStyled } from "../TabsStyled";
import { TextFieldStyled } from "../TextFieldStyled";
import IconComponent from "../../components/ui/IconComponent";
import { MinusIcon, PlusIcon } from "../../components/ui/icons/adronIcons";
import { IconButtonPaper } from "../IconButtonPaper";

interface Props {
  value: number;
  onChange: (value: number) => void;
}

const Leverage = ({ value, onChange }: Props) => {
  const theme = useTheme();

  const [isolated, setIsolated] = useState<number>(0);
  const handleLeverage = (event: React.SyntheticEvent | Event, newValue: number | number[]) =>
    onChange(newValue as number);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => setIsolated(newValue);

  const handleDecreaseStep = () => onChange(value + 1);
  const handleIncreaseStep = () => onChange(value - 1);

  const comingSoon = {
    label: "Isolated",
    icon: (
      <Paper elevation={0} sx={BADGE_PAPER_STYLES}>
        <Typography sx={{ fontWeight: 600, fontSize: 10 }} variant={"body2"} color="textSecondary">
          Soon
        </Typography>
      </Paper>
    ),
  };

  useEffect(() => {
    if (value < 1) {
      onChange(1);
    }
  }, [value]);

  return (
    <CustomPaper>
      <Stack direction={"column"} spacing={2} alignItems={"center"}>
        <Stack
          sx={{ width: "100%" }}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={"3px"}
            sx={{ marginBottom: "5px" }}
          >
            <AdronTypography value="Leverage" halfOpacity />
          </Stack>
          <TabContext value={isolated}>
            <TabsStyled style={{ borderRadius: 12 }} onChange={handleChange}>
              <Tab label={"Cross"} value={0} className="AdronSecondaryTabs" />
              <Tab
                disabled
                label={
                  <>
                    <Stack direction={"row"} alignItems={"center"} spacing={"4px"}>
                      {comingSoon.icon && (
                        <AdronTypography
                          halfOpacity
                          value={comingSoon.label}
                          style={{ marginRight: 4 }}
                        />
                      )}
                      {comingSoon.icon}
                    </Stack>
                  </>
                }
                value={false}
                className="AdronSecondaryTabs"
              />
            </TabsStyled>
          </TabContext>
        </Stack>
        <Stack direction={"column"} spacing={2} sx={{ width: "100%" }} alignItems={"center"}>
          <Stack
            sx={{ width: "100%" }}
            spacing={2}
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <TextFieldStyled
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              label=""
              variant="outlined"
              value={`${value}x`}
              size="small"
              onChange={(e) => {
								// Remove 'x' and convert to number
                const numericValue = parseFloat(e.target.value.replace(/x/g, ""));
                if (!isNaN(numericValue)) {
                  onChange(numericValue); // Pass numeric value to handler
                }
              }}
              required
            />
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <IconButtonPaper onClick={handleIncreaseStep}>
                <MinusIcon />
              </IconButtonPaper>
              <IconButtonPaper onClick={handleDecreaseStep}>
                <PlusIcon />
              </IconButtonPaper>
            </Stack>
          </Stack>
          <Slider
            size="medium"
            value={value}
            max={125} // Current Account Balance
            min={1}
            // marks
            onChange={handleLeverage}
            onChangeCommitted={handleLeverage}
            valueLabelDisplay="auto"
          />
        </Stack>
        <Stack direction={"column"} spacing={1} alignItems={"flex-start"}>
          <Stack direction={"row"} sx={{ opacity: 0.5 }} alignItems={"center"} spacing={1}>
            <IconComponent iconName={"DotFilled"} hasStroke />
            <AdronTypography value={"Maximum position at current leverage: 230 000 000 USDT"} />
          </Stack>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Box sx={{ opacity: 0 }}>
              <IconComponent iconName={"DotFilled"} hasStroke />
            </Box>
            <AdronTypography
              halfOpacity
              value={
                "Please note that leverage changing will just apply for current gris of Future Grid."
              }
            />
          </Stack>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <IconComponent iconName={"DotFilled"} hasStroke />
            <AdronTypography
              style={{ width: "100%" }}
              value={
                "Selecting higher leverage such as [10x] increases your liquidation risk. Always manage your risk levels."
              }
            />
          </Stack>
        </Stack>
      </Stack>
    </CustomPaper>
  );
};

export default Leverage;
